import { EventEmitter } from "eventemitter3";
import { ValidationError } from "./errors/protocol-error.js";
import { WaitForTimeoutError } from "./errors/timeout-error.js";
import {
  ConnectionError,
  DisconnectionError,
  SendError,
  TransportError,
} from "./errors/transport-error.js";
import { AddressValidationError } from "./errors/validation-error.js";
import { configureLogging, getLogger, hexDump } from "./internal/logger.js";
import { Protocol } from "./protocol.js";
import type { EventMsgConfig } from "./types/config.js";
import { DEFAULT_CONFIG } from "./types/config.js";
import {
  type ConnectionStats,
  INTERNAL_EVENTS,
  type MessageHandler,
  type MessageInfo,
} from "./types/events.js";
import type {
  MessageMetadata,
  MessageResult,
  PendingWait,
  SendOptions,
  WaitForOptions,
} from "./types/message.js";
import type { Transport } from "./types/transport.js";

/**
 * EventMsg - Main class for EventMsgV3 protocol communication
 *
 * Provides type-safe messaging with per-method generics, async event handling,
 * and transport abstraction.
 */
export class EventMsg<TDevice = unknown> extends EventEmitter {
  private readonly transport: Transport<TDevice>;
  protocol: Protocol;
  private readonly logger: ReturnType<typeof getLogger>;
  messageCounter = 0;
  private readonly config: Required<Omit<EventMsgConfig<TDevice>, "transport">>;
  private connectionState = false;
  private connectTime: Date | undefined;
  private readonly pendingWaits: Map<string, PendingWait[]> = new Map();
  private readonly stats: ConnectionStats;

  // Track message handlers for proper cleanup
  private readonly messageHandlers = new Map<
    string | symbol,
    ((messageInfo: MessageInfo) => void)[]
  >();

  constructor(config: EventMsgConfig<TDevice>) {
    super();

    // Merge with defaults
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.transport = config.transport;

    // Configure logging BEFORE creating Protocol or getting any loggers
    configureLogging(this.config.logging);

    // Now create Protocol (which will use the configured logger)
    this.protocol = new Protocol({ ...this.config, transport: this.transport });

    // Get logger for this component
    this.logger = getLogger("CORE");

    // Initialize stats
    this.stats = {
      messagesSent: 0,
      messagesReceived: 0,
      uptime: 0,
      connected: false,
    };

    // Bind transport events
    this.setupTransportHandlers();
  }

  /**
   * Connect to the transport layer
   */
  async connect(): Promise<void> {
    try {
      if (this.connectionState) {
        return; // Already connected
      }

      await this.transport.connect();
      this.connectionState = true;
      this.connectTime = new Date();
      this.stats.connected = true;

      this.emit(INTERNAL_EVENTS.CONNECT);

      this.logger.success("Connected to transport", {
        localAddress: this.transport.getLocalAddress(),
        groupAddress: this.transport.getGroupAddress(),
        uptime: 0,
      });
    } catch (error) {
      this.logger.error("Failed to connect to transport", {
        error: error instanceof Error ? error.message : String(error),
        localAddress: this.transport.getLocalAddress(),
        groupAddress: this.transport.getGroupAddress(),
        cause: error instanceof Error ? error.name : "Unknown",
      });

      const transportError = new ConnectionError(
        "Failed to connect to transport",
        {
          context: {
            localAddress: this.transport.getLocalAddress(),
            groupAddress: this.transport.getGroupAddress(),
          },
          cause: error instanceof Error ? error : new Error(String(error)),
        }
      );

      this.emit(INTERNAL_EVENTS.ERROR, transportError);
      throw transportError;
    }
  }

  /**
   * Disconnect from the transport layer
   */
  async disconnect(): Promise<void> {
    try {
      if (!this.connectionState) {
        return; // Already disconnected
      }

      // Clear all pending waits
      this.clearAllPendingWaits();

      // Clear all message handlers
      this.offMessage();

      await this.transport.disconnect();
      this.connectionState = false;

      this.stats.connected = false;

      this.emit(INTERNAL_EVENTS.DISCONNECT);

      this.logger.info("Disconnected from transport", {
        uptime: this.connectTime ? Date.now() - this.connectTime.getTime() : 0,
        messagesSent: this.stats.messagesSent,
        messagesReceived: this.stats.messagesReceived,
      });
    } catch (error) {
      this.logger.error("Failed to disconnect from transport", {
        error: error instanceof Error ? error.message : String(error),
        cause: error instanceof Error ? error.name : "Unknown",
        uptime: this.connectTime ? Date.now() - this.connectTime.getTime() : 0,
      });

      const transportError = new DisconnectionError(
        "Failed to disconnect from transport",
        {
          cause: error instanceof Error ? error : new Error(String(error)),
        }
      );

      this.emit(INTERNAL_EVENTS.ERROR, transportError);
      throw transportError;
    }
  }

  /**
   * Check if currently connected
   */
  isConnected(): boolean {
    return this.connectionState && this.transport.isConnected();
  }

  /**
   * Send a message with explicit receiver targeting
   */
  async send<TData = unknown>(
    event: string,
    data: TData,
    options: SendOptions
  ): Promise<void> {
    try {
      // Validate connection
      if (!this.isConnected()) {
        throw new ConnectionError("Not connected to transport");
      }

      // Validate inputs
      this.validateSendOptions(options);

      // Create message header
      const header = this.createHeader(options);

      // TODO: Check if we need type specific handle for event data (JSON, ARRAY)
      const eventData = data as string;

      // Encode message
      const encodedMessage = this.protocol.encode(header, event, eventData);

      this.logger.debug("Message encoded", {
        event,
        header,
        dataLength: eventData.length,
        encodedSize: encodedMessage.length,
      });

      // Log hex dump at trace level
      this.logger.trace("Encoded message hex dump", {
        hex: hexDump(encodedMessage),
      });
      // Send through transport
      await this.transport.send(encodedMessage);

      // Update stats
      this.stats.messagesSent++;

      // Log successful send
      this.logger.info("Message sent", {
        event,
        receiverId: options.receiverId,
        receiverGroupId: options.receiverGroupId,
        messageId: header.messageId,
        size: encodedMessage.length,
        totalSent: this.stats.messagesSent,
      });

      // Emit send event for debugging
      this.emit(INTERNAL_EVENTS.SEND, {
        event,
        data,
        options,
        header,
        messageSize: encodedMessage.length,
      });
    } catch (error) {
      this.logger.error("Failed to send message", {
        error: error instanceof Error ? error.message : String(error),
        event,
        receiverId: options.receiverId,
        receiverGroupId: options.receiverGroupId,
        dataLength: JSON.stringify(data).length,
        cause: error instanceof Error ? error.name : "Unknown",
      });

      const sendError = new SendError("Failed to send message", {
        context: {
          event,
          receiverId: options.receiverId,
          receiverGroupId: options.receiverGroupId,
          dataLength: JSON.stringify(data).length,
        },
        cause: error instanceof Error ? error : new Error(String(error)),
      });

      this.emit(INTERNAL_EVENTS.ERROR, sendError);
      throw sendError;
    }
  }

  /**
   * Send binary data with explicit receiver targeting
   */
  async sendBinary(
    event: string,
    data: Uint8Array,
    options: SendOptions
  ): Promise<void> {
    try {
      if (!this.isConnected()) {
        throw new ConnectionError("Not connected to transport");
      }

      this.validateSendOptions(options);

      const header = this.createHeader(options);

      const encodedMessage = this.protocol.encodeBinary(header, event, data);

      this.logger.debug("Binary message encoded", {
        event,
        header,
        dataLength: data.length,
        encodedSize: encodedMessage.length,
      });

      this.logger.trace("Encoded binary message hex dump", {
        hex: hexDump(encodedMessage),
      });

      await this.transport.send(encodedMessage);

      this.stats.messagesSent++;

      this.logger.info("Binary message sent", {
        event,
        receiverId: options.receiverId,
        receiverGroupId: options.receiverGroupId,
        messageId: header.messageId,
        size: encodedMessage.length,
        totalSent: this.stats.messagesSent,
      });

      this.emit(INTERNAL_EVENTS.SEND, {
        event,
        data,
        options,
        header,
        messageSize: encodedMessage.length,
      });
    } catch (error) {
      this.logger.error("Failed to send binary message", {
        error: error instanceof Error ? error.message : String(error),
        event,
        receiverId: options.receiverId,
        receiverGroupId: options.receiverGroupId,
        dataLength: data.length,
        cause: error instanceof Error ? error.name : "Unknown",
      });

      const sendError = new SendError("Failed to send binary message", {
        context: {
          event,
          receiverId: options.receiverId,
          receiverGroupId: options.receiverGroupId,
          dataLength: data.length,
        },
        cause: error instanceof Error ? error : new Error(String(error)),
      });

      this.emit(INTERNAL_EVENTS.ERROR, sendError);
      throw sendError;
    }
  }

  /**
   * Register a type-safe event handler
   */
  override on<TData = unknown>(
    event: string | symbol,
    handler: MessageHandler<TData>
  ): this {
    return super.on(event, handler);
  }

  /**
   * Register a one-time type-safe event handler
   */
  override once<TData = unknown>(
    event: string | symbol,
    handler: MessageHandler<TData>
  ): this {
    return super.once(event, handler);
  }

  /**
   * Remove a type-safe event handler
   */
  override off<TData = unknown>(
    event: string | symbol,
    handler?: MessageHandler<TData>
  ): this {
    if (handler) {
      return super.off(event, handler);
    }
    return super.removeAllListeners(event);
  }

  /**
   * Register a handler for all incoming user messages
   * @param handler - Function called for every incoming message
   * @returns Unsubscribe function to remove this specific handler
   * @example
   * ```typescript
   * const unsubscribe = eventMsg.onMessage((eventName, data, metadata) => {
   *   console.log(`Received ${eventName} from device ${metadata.senderId}`);
   * });
   * // Later: unsubscribe();
   * ```
   */
  onMessage(
    handler: (
      eventName: string,
      data: unknown,
      metadata: MessageMetadata
    ) => void
  ): () => void;
  /**
   * Register a handler for a specific message event type
   * @param eventName - The specific event name to listen for
   * @param handler - Function called when the specific event is received
   * @returns Unsubscribe function to remove this specific handler
   * @example
   * ```typescript
   * const unsubscribe = eventMsg.onMessage('ping', (data, metadata) => {
   *   console.log(`Ping from device ${metadata.senderId}: ${data}`);
   * });
   * // Later: unsubscribe();
   * ```
   */
  onMessage<TData = unknown>(
    eventName: string,
    handler: (data: TData, metadata: MessageMetadata) => void
  ): () => void;
  onMessage<TData = unknown>(
    eventNameOrHandler:
      | string
      | ((eventName: string, data: unknown, metadata: MessageMetadata) => void),
    handler?: (data: TData, metadata: MessageMetadata) => void
  ): () => void {
    if (typeof eventNameOrHandler === "string") {
      // Specific event subscription
      const targetEventName = eventNameOrHandler;
      const specificHandler = handler;

      const internalHandler = (messageInfo: MessageInfo) => {
        if (messageInfo.eventName === targetEventName && specificHandler) {
          // Type assertion is safe here because user controls TData generic
          specificHandler(messageInfo.data as TData, messageInfo.metadata);
        }
      };

      this.on(INTERNAL_EVENTS.MESSAGE, internalHandler);
      this.trackMessageHandler(targetEventName, internalHandler);

      return () => {
        this.off(INTERNAL_EVENTS.MESSAGE, internalHandler);
        const handlers = this.messageHandlers.get(targetEventName);
        if (handlers) {
          const index = handlers.indexOf(internalHandler);
          if (index > -1) {
            handlers.splice(index, 1);
            if (handlers.length === 0) {
              this.messageHandlers.delete(targetEventName);
            }
          }
        }
      };
    }
    // Catch-all subscription
    const catchAllHandler = eventNameOrHandler;
    const catchAllKey = Symbol("catch-all");

    const internalHandler = (messageInfo: MessageInfo) => {
      catchAllHandler(
        messageInfo.eventName,
        messageInfo.data,
        messageInfo.metadata
      );
    };

    this.on(INTERNAL_EVENTS.MESSAGE, internalHandler);
    this.trackMessageHandler(catchAllKey, internalHandler);

    return () => {
      this.off(INTERNAL_EVENTS.MESSAGE, internalHandler);
      const handlers = this.messageHandlers.get(catchAllKey);
      if (handlers) {
        const index = handlers.indexOf(internalHandler);
        if (index > -1) {
          handlers.splice(index, 1);
          if (handlers.length === 0) {
            this.messageHandlers.delete(catchAllKey);
          }
        }
      }
    };
  }

  /**
   * Remove message handlers
   * @param eventName - The event name to stop listening for
   * @returns this instance for chaining
   * @warning When called without arguments, removes ALL message handlers from ALL events.
   * Prefer using the unsubscribe function returned by onMessage() for targeted cleanup.
   * @example
   * ```typescript
   * // Preferred: use unsubscribe function from onMessage()
   * const unsubscribe = eventMsg.onMessage('ping', handler);
   * unsubscribe(); // Removes only this handler
   *
   * // Remove all handlers for a specific event
   * eventMsg.offMessage('ping');
   *
   * // Remove ALL message handlers (use with caution)
   * eventMsg.offMessage();
   * ```
   */
  offMessage(eventName?: string): this {
    if (eventName) {
      // Remove specific event handlers
      const handlers = this.messageHandlers.get(eventName);
      if (handlers) {
        for (const handler of handlers) {
          this.off(INTERNAL_EVENTS.MESSAGE, handler);
        }
        this.messageHandlers.delete(eventName);
      }
    } else {
      // Remove all message handlers
      for (const [, handlers] of this.messageHandlers) {
        for (const handler of handlers) {
          this.off(INTERNAL_EVENTS.MESSAGE, handler);
        }
      }
      this.messageHandlers.clear();
    }

    return this;
  }

  /**
   * Track message handlers for proper cleanup
   */
  private trackMessageHandler(
    key: string | symbol,
    handler: (messageInfo: MessageInfo) => void
  ): void {
    if (!this.messageHandlers.has(key)) {
      this.messageHandlers.set(key, []);
    }
    this.messageHandlers.get(key)?.push(handler);
  }

  /**
   * Wait for a single event occurrence with timeout
   */
  waitFor<TData = unknown>(
    event: string,
    options: WaitForOptions = {}
  ): Promise<MessageResult<TData>> {
    return new Promise((resolve, reject) => {
      const timeout = options.timeout ?? this.config.messageTimeout;

      const pendingWait: PendingWait<TData> = {
        resolve,
        reject,
        timeout: setTimeout(() => {
          this.removePendingWait(event, pendingWait);
          reject(new WaitForTimeoutError(event, timeout));
        }, timeout),
        filter: options.filter ?? undefined,
      };

      this.addPendingWait(event, pendingWait);

      this.logger.debug("Waiting for event", {
        event,
        timeout,
        hasFilter: !!options.filter,
        pendingWaits: this.pendingWaits.get(event)?.length ?? 0,
      });
    });
  }

  /**
   * Get local device address from transport
   */
  getLocalAddress(): number {
    return this.transport.getLocalAddress();
  }

  /**
   * Get local group address from transport
   */
  getGroupAddress(): number {
    return this.transport.getGroupAddress();
  }

  /**
   * Get connection statistics
   */
  getStats(): ConnectionStats {
    return {
      ...this.stats,
      uptime: this.connectTime ? Date.now() - this.connectTime.getTime() : 0,
    };
  }

  /**
   * Get the underlying transport instance
   */
  getTransport(): Transport<TDevice> {
    return this.transport;
  }

  /**
   * Get the connected device from transport
   */
  getDevice(): TDevice | null {
    return this.transport.getDevice();
  }

  /**
   * Create message header from send options
   */
  private createHeader(
    options: SendOptions
  ): import("./types/message.js").MessageHeader {
    return {
      senderId: this.transport.getLocalAddress(),
      receiverId: options.receiverId,
      senderGroupId: this.transport.getGroupAddress(),
      receiverGroupId: options.receiverGroupId ?? 0x00,
      flags: options.flags ?? 0x00,
      messageId: ++this.messageCounter & 0xff_ff, // Wrap at 16-bit
    };
  }

  /**
   * Validate send options
   */
  private validateSendOptions(options: SendOptions): void {
    if (
      options.receiverId < 0 ||
      options.receiverId > 255 ||
      !Number.isInteger(options.receiverId)
    ) {
      throw new AddressValidationError(options.receiverId, "receiverId");
    }

    if (
      options.receiverGroupId !== undefined &&
      (options.receiverGroupId < 0 ||
        options.receiverGroupId > 255 ||
        !Number.isInteger(options.receiverGroupId))
    ) {
      throw new AddressValidationError(
        options.receiverGroupId,
        "receiverGroupId"
      );
    }

    if (
      options.flags !== undefined &&
      (options.flags < 0 ||
        options.flags > 255 ||
        !Number.isInteger(options.flags))
    ) {
      throw new ValidationError("flags must be 0-255", {
        context: {
          field: "flags",
          value: options.flags,
        },
      });
    }
  }

  /**
   * Setup transport event handlers
   */
  private setupTransportHandlers(): void {
    this.transport.on("data", this.handleIncomingData.bind(this));
    this.transport.on("error", this.handleTransportError.bind(this));
    this.transport.on("disconnect", this.handleTransportDisconnect.bind(this));
  }

  /**
   * Handle incoming transport data
   */
  private handleIncomingData(data: Uint8Array): void {
    try {
      // Decode message
      const decoded = this.protocol.decode(data);

      // Create metadata
      const metadata: MessageMetadata = {
        senderId: decoded.header.senderId,
        senderGroupId: decoded.header.senderGroupId,
        receiverId: decoded.header.receiverId,
        receiverGroupId: decoded.header.receiverGroupId,
        messageId: decoded.header.messageId,
        flags: decoded.header.flags,
        timestamp: new Date(),
      };

      // Parse event data
      let eventData: unknown;
      try {
        eventData = JSON.parse(decoded.eventData);
      } catch {
        eventData = decoded.eventData; // Fall back to string if not JSON
      }

      const messageResult: MessageResult<unknown> = {
        data: eventData,
        metadata,
      };

      // Update stats
      this.stats.messagesReceived++;

      // Log message received
      this.logger.info("Message received", {
        event: decoded.eventName,
        senderId: metadata.senderId,
        senderGroupId: metadata.senderGroupId,
        messageId: metadata.messageId,
        size: data.length,
        totalReceived: this.stats.messagesReceived,
      });

      // Log hex dump at trace level
      this.logger.trace("Received message hex dump", {
        hex: hexDump(data),
      });

      // Resolve pending waits
      this.resolvePendingWaits(decoded.eventName, messageResult);

      // Emit message event for onMessage handlers
      const messageInfo: MessageInfo = {
        eventName: decoded.eventName,
        data: eventData,
        metadata,
        messageSize: data.length,
      };

      this.emit(INTERNAL_EVENTS.MESSAGE, messageInfo);
    } catch (error) {
      this.logger.error("Failed to process incoming message", {
        error: error instanceof Error ? error.message : String(error),
        messageLength: data.length,
        cause: error instanceof Error ? error.name : "Unknown",
        hex: hexDump(data, 64),
      });

      const protocolError = new TransportError(
        "Failed to process incoming message",
        {
          context: { messageLength: data.length },
          cause: error instanceof Error ? error : new Error(String(error)),
        }
      );

      this.emit(INTERNAL_EVENTS.ERROR, protocolError);
    }
  }

  /**
   * Handle transport errors
   */
  private handleTransportError(error: Error): void {
    this.logger.error("Transport error occurred", {
      error: error.message,
      cause: error.name,
      connected: this.connectionState,
      uptime: this.connectTime ? Date.now() - this.connectTime.getTime() : 0,
    });

    const transportError = new TransportError("Transport error occurred", {
      cause: error,
    });

    this.stats.lastError = new Date();
    this.emit(INTERNAL_EVENTS.ERROR, transportError);
  }

  /**
   * Handle transport disconnection
   */
  private handleTransportDisconnect(): void {
    const uptime = this.connectTime
      ? Date.now() - this.connectTime.getTime()
      : 0;

    this.logger.warn("Transport disconnected unexpectedly", {
      uptime,
      messagesSent: this.stats.messagesSent,
      messagesReceived: this.stats.messagesReceived,
      pendingWaits: this.pendingWaits.size,
    });

    this.connectionState = false;
    this.connectTime = undefined;
    this.stats.connected = false;

    // Clear pending waits
    this.clearAllPendingWaits();

    this.emit(INTERNAL_EVENTS.DISCONNECT);
  }

  /**
   * Add a pending wait for an event
   */
  private addPendingWait<TData>(
    event: string,
    pendingWait: PendingWait<TData>
  ): void {
    if (!this.pendingWaits.has(event)) {
      this.pendingWaits.set(event, []);
    }
    this.pendingWaits.get(event)?.push(pendingWait as PendingWait<unknown>);
  }

  /**
   * Remove a pending wait
   */
  private removePendingWait<TData>(
    event: string,
    pendingWait: PendingWait<TData>
  ): void {
    const waits = this.pendingWaits.get(event);
    if (waits) {
      const index = waits.indexOf(pendingWait as PendingWait<unknown>);
      if (index !== -1) {
        clearTimeout(pendingWait.timeout);
        waits.splice(index, 1);

        if (waits.length === 0) {
          this.pendingWaits.delete(event);
        }
      }
    }
  }

  /**
   * Resolve pending waits for an event
   */
  private resolvePendingWaits(
    event: string,
    messageResult: MessageResult<unknown>
  ): void {
    const waits = this.pendingWaits.get(event);
    if (!waits || waits.length === 0) {
      return;
    }

    // Create a copy to iterate over, as we'll be modifying the original array
    const waitsToResolve = [...waits];

    for (const wait of waitsToResolve) {
      // Check filter condition
      if (wait.filter && !wait.filter(messageResult.metadata)) {
        continue; // Skip this wait, filter didn't match
      }

      // Remove from pending waits
      this.removePendingWait(event, wait);

      // Resolve the promise
      wait.resolve(messageResult);
    }
  }

  /**
   * Clear all pending waits (on disconnect)
   */
  private clearAllPendingWaits(): void {
    for (const [, waits] of this.pendingWaits.entries()) {
      for (const wait of waits) {
        clearTimeout(wait.timeout);
        wait.reject(
          new DisconnectionError("Connection lost while waiting for event")
        );
      }
    }
    this.pendingWaits.clear();
  }
}
